import { useEffect, useState } from 'react';
import './style.scss';
import TravellerCategorySelector from '../TravellerCategorySelector';
import ProductTypeTable from './components/ProductTypeTable';
import GroupedTicketSuggestions from './models/grouped-ticket-suggestions.model';
import { Channel } from '../../../models/channel.enum';
import { ProductType } from '../../../models/product-type.enum';
import { TravellerCategory } from '../../../models/traveller-category.enum';
import { TicketSuggestionHelper } from '../../../helpers/ticket-suggestion.helper';
import { TranslationService } from '../../../services/translation.service';
import { Product } from '../../../models/product.enum';
import type { ITicketSuggestion } from '../../../models/ticket-suggestion.model';

// === FUNCTIONS ===
function addYouthTapAndGoTicket(clonedArray: ITicketSuggestion[]) {
  const someYouthTicketHasTapAndGo = clonedArray.some(
    (ticketSuggestion: ITicketSuggestion) => ticketSuggestion.travellerCategory === TravellerCategory.youth && ticketSuggestion.saleChannels.some((saleChannel) => saleChannel.id === Channel.tapAndGo)
  );

  if (someYouthTicketHasTapAndGo) {
    return;
  }
  const adultTapAndGoTickets = clonedArray.filter((ticketSuggestion) => ticketSuggestion.productId === Product.singleZoneAAdultTapAndGo);
  if (adultTapAndGoTickets.length === 0) {
    return;
  }
  const adultTapAndGoTicket = adultTapAndGoTickets[0];
  const youthTapAndGoTicket = { ...adultTapAndGoTicket };

  youthTapAndGoTicket.productId = Product.singleZoneAYouthTapAndGo;
  youthTapAndGoTicket.travellerCategory = TravellerCategory.youth;
  youthTapAndGoTicket.productName = 'Zon A 90 minuter Ungdom';

  clonedArray.push(youthTapAndGoTicket);
}

function filterAndGroupTicketSuggestions(ticketSuggestions: ITicketSuggestion[]) {
  const groupedTicketSuggestions = new GroupedTicketSuggestions();
  if (!ticketSuggestions || ticketSuggestions.length === 0) {
    return groupedTicketSuggestions;
  }
  const clonedArray = ticketSuggestions;
  addYouthTapAndGoTicket(clonedArray);
  clonedArray
    .sort((a, b) => a.priceInSek - b.priceInSek)
    .forEach((ticket) => {
      const validChannelAndTimeLimitation = TicketSuggestionHelper.hasValidSalesChannelsAndTimeLimitation(ticket);
      if (!validChannelAndTimeLimitation) {
        return;
      }
      groupedTicketSuggestions.addTicket(ticket);
    });
  return groupedTicketSuggestions;
}

function TicketSuggestions({
  ticketSuggestions,
  restrictedToProductTypes,
  onTravellerCategoryChange,
}: {
  ticketSuggestions: ITicketSuggestion[];
  restrictedToProductTypes?: ProductType[];
  onTravellerCategoryChange?: (category: TravellerCategory) => void;
}) {
  // === STATE ===
  const [currentTravellerCategory, setCurrentTravellerCategory] = useState(TravellerCategory.adult);
  const [translationNoTicketsFoundError] = useState(TranslationService.get('travelplanner.nopricesfound'));

  function showProductType(productType: ProductType) {
    if (restrictedToProductTypes === undefined || restrictedToProductTypes.length < 1) {
      return true;
    }
    return restrictedToProductTypes.includes(productType);
  }

  // === VARIABLES ===
  const groupedTicketSuggestions = filterAndGroupTicketSuggestions(ticketSuggestions);
  const hasTickets = groupedTicketSuggestions.hasTickets();
  const showSingleTickets =
    showProductType(ProductType.singleTicket) &&
    groupedTicketSuggestions.singleTickets.length > 0 &&
    groupedTicketSuggestions.singleTickets.some((s) => s.hasTravellerCategory.includes(currentTravellerCategory));

  const showPrepaidTickets =
    showProductType(ProductType.prepaidTicket) &&
    groupedTicketSuggestions.prepaidTickets.length > 0 &&
    groupedTicketSuggestions.prepaidTickets.some((s) => s.hasTravellerCategory.includes(currentTravellerCategory));

  const showPeriodTickets =
    showProductType(ProductType.periodTicket) &&
    groupedTicketSuggestions.periodTickets.length > 0 &&
    groupedTicketSuggestions.periodTickets.some((s) => s.hasTravellerCategory.includes(currentTravellerCategory));

  const showDayTickets =
    showProductType(ProductType.dayTicket) &&
    groupedTicketSuggestions.dayTickets.length > 0 &&
    groupedTicketSuggestions.dayTickets.some((s) => s.hasTravellerCategory.includes(currentTravellerCategory));

  const showFlexTickets =
    showProductType(ProductType.flexTicket) &&
    groupedTicketSuggestions.flexTickets.length > 0 &&
    groupedTicketSuggestions.flexTickets.some((s) => s.hasTravellerCategory.includes(currentTravellerCategory));

  const showPartialYearTickets =
    showProductType(ProductType.partialYearTicket) &&
    groupedTicketSuggestions.partialYearTickets.length > 0 &&
    groupedTicketSuggestions.partialYearTickets.some((s) => s.hasTravellerCategory.includes(currentTravellerCategory));

  // === EFFECTS ===
  useEffect(() => {
    onTravellerCategoryChange?.(currentTravellerCategory);
  }, [onTravellerCategoryChange, currentTravellerCategory]);

  return (
    <div className="ticket-suggestions">
      {hasTickets ? (
        <>
          <TravellerCategorySelector selectedTravellerCategory={currentTravellerCategory} onChange={setCurrentTravellerCategory} renderAsDropdown={false} />
          <ul className="list-unstyled ticket-suggestions__list">
            {showSingleTickets && (
              <li className={`ticket-suggestions__list-item ${showPrepaidTickets || showPartialYearTickets || showPeriodTickets || showFlexTickets || showDayTickets ? 'border-bottom' : ''}`}>
                <ProductTypeTable productType={ProductType.singleTicket} ticketSuggestions={groupedTicketSuggestions.singleTickets} currentTravellerCategory={currentTravellerCategory} />
              </li>
            )}
            {showPrepaidTickets && (
              <li className={`ticket-suggestions__list-item ${showPartialYearTickets || showPeriodTickets || showFlexTickets || showDayTickets ? ' border-bottom' : ''}`}>
                <ProductTypeTable productType={ProductType.prepaidTicket} ticketSuggestions={groupedTicketSuggestions.prepaidTickets} currentTravellerCategory={currentTravellerCategory} />
              </li>
            )}
            {showPartialYearTickets && (
              <li className={`ticket-suggestions__list-item ${showPeriodTickets || showFlexTickets || showDayTickets ? 'border-bottom' : ''}`}>
                <ProductTypeTable productType={ProductType.partialYearTicket} ticketSuggestions={groupedTicketSuggestions.partialYearTickets} currentTravellerCategory={currentTravellerCategory} />
              </li>
            )}
            {showPeriodTickets && (
              <li className={`ticket-suggestions__list-item ${showFlexTickets || showDayTickets ? 'border-bottom' : ''}`}>
                <ProductTypeTable productType={ProductType.periodTicket} ticketSuggestions={groupedTicketSuggestions.periodTickets} currentTravellerCategory={currentTravellerCategory} />
              </li>
            )}
            {showFlexTickets && (
              <li className={`ticket-suggestions__list-item ${showDayTickets ? 'border-bottom' : ''}`}>
                <ProductTypeTable productType={ProductType.flexTicket} ticketSuggestions={groupedTicketSuggestions.flexTickets} currentTravellerCategory={currentTravellerCategory} />
              </li>
            )}
            {showDayTickets && (
              <li className="ticket-suggestions__list-item">
                <ProductTypeTable productType={ProductType.dayTicket} ticketSuggestions={groupedTicketSuggestions.dayTickets} currentTravellerCategory={currentTravellerCategory} />
              </li>
            )}
          </ul>
        </>
      ) : (
        <div className="text-center p-3">{translationNoTicketsFoundError}</div>
      )}
    </div>
  );
}

export default TicketSuggestions;
